"use client";

import { useQuery } from "@tanstack/react-query";
import { notFound } from "next/navigation";
import { createContext, useContext, useMemo } from "react";
import { useGraphql } from "~/api-client";
import type { LocationOrderingEnabled, RichLocation } from "~/types";
import {
  fetchJetsLocationByRef,
  type JetsLocationWithRef,
} from "~/utils/jets-locations";
import { mapRichLocation } from "~/utils/location";

interface LocationContext {
  locationRef: string;
  location?: RichLocation;
  loading: boolean;
}

const locationContext = createContext<LocationContext>({
  locationRef: "",
  location: undefined,
  loading: true,
});

export function useJetsLocation(): {
  location: JetsLocationWithRef | null;
  loading: boolean;
} {
  const { locationRef } = useLocation();

  const { data, isLoading } = useQuery({
    queryKey: ["jets-location", locationRef],
    queryFn: () => fetchJetsLocationByRef(locationRef),
    enabled: Boolean(locationRef),
    staleTime: Infinity,
    gcTime: Infinity,
  });

  return {
    loading: !locationRef || isLoading,
    location: data ?? null,
  };
}

export function LocationProvider({
  locationRef,
  children,
}: {
  locationRef: string;
  children: React.ReactNode;
}) {
  const { locations, loading: loadingLocations } = useLocations();

  const value = useMemo(
    () => ({
      locationRef,
      location: locations?.find((location) => location.ref === locationRef),
      loading: loadingLocations,
    }),
    [locationRef, locations, loadingLocations],
  );

  if (loadingLocations && !value.location) return null;
  if (!value.location) notFound();

  return (
    <locationContext.Provider value={value}>
      {children}
    </locationContext.Provider>
  );
}

export function useLocation(): LocationContext {
  return useContext(locationContext);
}

export function useLocationOrderingEnabled(locationRef?: string): {
  loading: boolean;
  orderingEnabled?: LocationOrderingEnabled;
} {
  const { getLocationOrderingEnabled } = useGraphql();
  const { data, isLoading } = useQuery({
    queryKey: ["loadingOrderingAvailable", locationRef],
    queryFn: async () => {
      if (!locationRef) throw new Error("Missing location ref");
      const data = await getLocationOrderingEnabled(locationRef);
      if (!data.location) return null;
      return {
        orderAheadEnabled: data.location.orderAheadEnabled,
        orderAtTableEnabled: data.location.orderAtTableEnabled,
        orderDeliveryEnabled: data.location.orderDeliveryEnabled,
      };
    },
    enabled: Boolean(locationRef),
  });

  return {
    loading: isLoading,
    orderingEnabled: data
      ? {
          orderAheadEnabled: data.orderAheadEnabled,
          orderAtTableEnabled: data.orderAtTableEnabled,
          orderDeliveryEnabled: data.orderDeliveryEnabled,
        }
      : undefined,
  };
}

export function useLocations(): {
  merchantName: string | undefined;
  locations: RichLocation[] | undefined;
  loading: boolean;
} {
  const { getLocations } = useGraphql();
  const { data, isLoading } = useQuery({
    queryKey: ["locations"],
    queryFn: async () => {
      const data = await getLocations();
      const locations = data.locations?.map(mapRichLocation) ?? [];
      return {
        merchantName: data.merchant.name,
        locations,
      };
    },
    staleTime: Infinity,
    gcTime: Infinity,
  });

  return {
    loading: isLoading,
    merchantName: data?.merchantName,
    locations: data?.locations,
  };
}
